import { getUniqueGradeBodyTypeId } from "../../../../common-deprecated/features/apheleia/utils/aphUtils";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import { EcoTagType, type PriceConfigType } from "../../../../common-deprecated/types/CommonTypes";
import { ecoTagToLabel } from "../../../../common-deprecated/utils/ecoTagUtils";
import type { GradeSelectorSettingsType } from "../redux/reducers/GradeSelectorSettingsReducer";
import type { GradeSelectorFiltersType } from "../redux/slices/GradeSelectorFiltersSlice";
import { GradeSelectorFilterId, isMultipleChoiceFilter, SortingType } from "./filterConstants";
import { getGradeSelectorLabel, type GradeSelectorLabelType } from "./labels";
import type {
    GradeBodyTypeEngineTransmissionType,
    GradeBodyTypeEngineType,
    GradeBodyTypeType,
    PriceRangeType,
} from "./types/Model";
import { getEcoTagType } from "../../shared-logic/utils/ecoUtils";
import type { LcvFilterCarType } from "./types/LcvFilter";
import { getCarDBAssetUrl } from "../../../shared-logic/utils/imageUtils";
import type {
    ApiGradeBodyEngineTransmissionWheeldriveCarType,
    SBRockarCarType,
} from "../../../../common-deprecated/types/ApiModel";
import { calculatePriceWithColour } from "./gradeUtils";
import { AemFmPriceType } from "../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";

export type FilterDataType = {
    uniqueGradeBodyTypeIds: string[];
    code: string;
    name: string;
    swatch?: { asset?: string; rgb?: string };
    sortable?: string;
    price?: PriceRangeType;
};

/**
 * Returns the possible filters for the current grades.
 *
 * @param filterId The type of filter which needs this data.
 * @param grades The active grades.
 * @param commonSettings Needed to get the correct ecoTag label.
 * @param gradeSelectorSettings to get the correct ecoTag label.
 * @param priceConfig - Needed for calculating prices with colors
 * @returns FilterDataType[] which contains the name of the possible filter values, the amount of corresponding grades...
 * and in case of colour, the asset + RGB and id of the colour.
 */
export const getFilterData = (
    filterId: GradeSelectorFilterId,
    grades: GradeBodyTypeType[],
    commonSettings: CommonSettingsType,
    gradeSelectorSettings: GradeSelectorSettingsType,
    priceConfig: PriceConfigType,
): FilterDataType[] => {
    const result: { [key: string]: FilterDataType } = {};

    const enginesPerGradyBodyType = grades.map(
        ({ engines, grade, bodyType, submodel, equipment, selectedColourId }) => {
            const uniqueId = getUniqueGradeBodyTypeId(grade.id, bodyType.id, submodel?.id);
            return { engines, uniqueId, equipment, selectedColourId };
        },
    );

    switch (filterId) {
        // Find all the available engine types (ecoTags).
        case GradeSelectorFilterId.Engine: {
            enginesPerGradyBodyType.forEach(({ engines, uniqueId }) => {
                engines.forEach((engine) => {
                    const ecoTag = getEcoTagType(engine);
                    if (ecoTag) {
                        if (result[ecoTag]) {
                            if (!result[ecoTag].uniqueGradeBodyTypeIds.includes(uniqueId)) {
                                result[ecoTag].uniqueGradeBodyTypeIds.push(uniqueId);
                            }
                        } else {
                            const ecoTagLabel = ecoTagToLabel(ecoTag);
                            const label = ecoTagLabel
                                ? getGradeSelectorLabel({ gradeSelectorSettings, commonSettings }, ecoTagLabel)
                                : ecoTag;
                            result[ecoTag] = {
                                uniqueGradeBodyTypeIds: [uniqueId],
                                code: ecoTag,
                                name: label,
                            };
                        }
                    }
                });
            });
            break;
        }
        // Find all the available wheeldrives (FWD, AWD, ...).
        case GradeSelectorFilterId.WheelDrive: {
            enginesPerGradyBodyType.forEach(({ engines, uniqueId }) => {
                engines.forEach(({ transmissions }) =>
                    transmissions.forEach(({ wheeldrives }) =>
                        wheeldrives.forEach(({ code, name }) => {
                            if (result[code]) {
                                if (!result[code].uniqueGradeBodyTypeIds.includes(uniqueId)) {
                                    result[code].uniqueGradeBodyTypeIds.push(uniqueId);
                                }
                            } else {
                                result[code] = { uniqueGradeBodyTypeIds: [uniqueId], code, name };
                            }
                        }),
                    ),
                );
            });
            break;
        }
        // Find all the available transmission types (manual, automatic, ...).
        case GradeSelectorFilterId.Transmission: {
            enginesPerGradyBodyType.forEach(({ engines, uniqueId }) => {
                engines.forEach(({ transmissions }) =>
                    transmissions.forEach(({ code, name }) => {
                        if (result[code]) {
                            if (!result[code].uniqueGradeBodyTypeIds.includes(uniqueId)) {
                                result[code].uniqueGradeBodyTypeIds.push(uniqueId);
                            }
                        } else {
                            result[code] = { uniqueGradeBodyTypeIds: [uniqueId], code, name };
                        }
                    }),
                );
            });
            break;
        }
        // Find all the available colours.
        case GradeSelectorFilterId.Colour: {
            enginesPerGradyBodyType.forEach(({ engines, uniqueId }) => {
                engines.forEach(({ transmissions }) =>
                    transmissions.forEach(({ wheeldrives }) =>
                        wheeldrives.forEach(({ colors }) => {
                            return colors.forEach(({ name, id, asset, rgb, type }) => {
                                if (result[id]) {
                                    if (!result[id].uniqueGradeBodyTypeIds.includes(uniqueId)) {
                                        result[id].uniqueGradeBodyTypeIds.push(uniqueId);
                                    }
                                } else {
                                    result[id] = {
                                        uniqueGradeBodyTypeIds: [uniqueId],
                                        code: id,
                                        name: `${type} - ${name}`,
                                        swatch: {
                                            asset: getCarDBAssetUrl(asset, commonSettings.compressedCcisPath, 48, 48),
                                            rgb,
                                        },
                                        sortable: type,
                                    };
                                }
                            });
                        }),
                    ),
                );
            });

            break;
        }
        // Find the price range.
        case GradeSelectorFilterId.Price: {
            // Get all valid prices from the data structure
            const allPrices = enginesPerGradyBodyType.flatMap(({ engines, selectedColourId }) =>
                engines.flatMap((engine) =>
                    engine.transmissions.flatMap((transmission) =>
                        transmission.wheeldrives
                            .map((wheeldrive) => {
                                // Apply your pricing logic for each wheeldrive
                                const { price } = wheeldrive;
                                const priceToUse =
                                    priceConfig.priceType === AemFmPriceType.InclVat
                                        ? price.listWithDiscount
                                        : price.netWithDiscount;

                                if (!priceConfig.priceEnabled || !priceToUse) {
                                    return null;
                                }

                                const colour = wheeldrive.colors.find((color) => color.id === selectedColourId);

                                return calculatePriceWithColour(
                                    priceConfig.priceType === AemFmPriceType.InclVat,
                                    priceToUse,
                                    colour?.price,
                                );
                            })
                            .filter((price): price is number => price !== null && price !== undefined),
                    ),
                ),
            );

            if (allPrices.length > 0) {
                // Find min and max prices
                const minPrice = Math.min(...allPrices);
                const maxPrice = Math.max(...allPrices);

                // Round to nearest thousand
                const roundedMinPrice = Math.floor(minPrice / 1000) * 1000;
                const roundedMaxPrice = Math.ceil(maxPrice / 1000) * 1000;

                result[filterId] = {
                    uniqueGradeBodyTypeIds: enginesPerGradyBodyType.map(({ uniqueId }) => uniqueId),
                    code: filterId,
                    name: "Price Range",
                    price: {
                        minPrice: roundedMinPrice,
                        maxPrice: roundedMaxPrice,
                    },
                };
            }

            break;
        }

        // Find/add sorting options.
        case GradeSelectorFilterId.Sort: {
            const allGradeBodyTypeIds = enginesPerGradyBodyType.map(({ uniqueId }) => uniqueId);

            const [
                gradeSelectorSortingCashPrice,
                gradeSelectorSortingWltp,
                gradeSelectorSortingFuelEfficiency,
                gradeSelectorSortingAsc,
                gradeSelectorSortingDesc,
            ] = (
                [
                    "gradeSelectorSortingCashPrice",
                    "gradeSelectorSortingWltp",
                    "gradeSelectorSortingFuelEfficiency",
                    "gradeSelectorSortingAsc",
                    "gradeSelectorSortingDesc",
                ] as GradeSelectorLabelType[]
            ).map((key) => getGradeSelectorLabel({ commonSettings, gradeSelectorSettings }, key));

            // Check if specific specs exist in the data
            const hasSpec = (specName: string): boolean => {
                return enginesPerGradyBodyType.some(({ engines }) =>
                    engines.some((engine) =>
                        engine.transmissions.some((transmission) =>
                            transmission.wheeldrives.some((wheeldrive) =>
                                wheeldrive.specs.some((spec) => spec.code === specName && spec.value),
                            ),
                        ),
                    ),
                );
            };

            // Define sorting options conditionally
            const sortingOptions = [];

            // Add price sorting options
            sortingOptions.push(
                {
                    code: SortingType.CashPriceAsc,
                    name: `${gradeSelectorSortingCashPrice} - ${gradeSelectorSortingAsc}`,
                },
                {
                    code: SortingType.CashPriceDesc,
                    name: `${gradeSelectorSortingCashPrice} - ${gradeSelectorSortingDesc}`,
                },
            );

            // Only add WLTP sorting if WLTP spec exists
            if (hasSpec("co2combinedwltp")) {
                sortingOptions.push(
                    { code: SortingType.WltpAsc, name: `${gradeSelectorSortingWltp} - ${gradeSelectorSortingAsc}` },
                    { code: SortingType.WltpDesc, name: `${gradeSelectorSortingWltp} - ${gradeSelectorSortingDesc}` },
                );
            }

            // Only add fuel efficiency sorting if fuel efficiency spec exists
            if (hasSpec("fuelcombinedwltp")) {
                sortingOptions.push(
                    {
                        code: SortingType.FuelEfficiencyAsc,
                        name: `${gradeSelectorSortingFuelEfficiency} - ${gradeSelectorSortingAsc}`,
                    },
                    {
                        code: SortingType.FuelEfficiencyDesc,
                        name: `${gradeSelectorSortingFuelEfficiency} - ${gradeSelectorSortingDesc}`,
                    },
                );
            }

            // Create sorting options only for those that have data
            sortingOptions.forEach((option) => {
                result[option.code] = {
                    uniqueGradeBodyTypeIds: allGradeBodyTypeIds,
                    code: option.code,
                    name: option.name,
                };
            });

            break;
        }

        // Find popular features.
        case GradeSelectorFilterId.PopularFeatures: {
            enginesPerGradyBodyType.forEach(({ equipment, uniqueId }) => {
                equipment.forEach((feature) => {
                    // Only include features that are marked as key features (popular features)
                    if (feature.isKeyFeature) {
                        if (result[feature.id]) {
                            if (!result[feature.id].uniqueGradeBodyTypeIds.includes(uniqueId)) {
                                result[feature.id].uniqueGradeBodyTypeIds.push(uniqueId);
                            }
                        } else {
                            result[feature.id] = {
                                uniqueGradeBodyTypeIds: [uniqueId],
                                code: feature.id.toString(),
                                name: feature.name,
                            };
                        }
                    }
                });
            });

            // Filter out features that are available for all grades
            const totalUniqueIds = enginesPerGradyBodyType.map(({ uniqueId }) => uniqueId);
            const allGradesCount = totalUniqueIds.length;

            // Remove features that are available for all grades
            Object.keys(result).forEach((featureId) => {
                if (result[featureId].uniqueGradeBodyTypeIds.length === allGradesCount) {
                    delete result[featureId];
                }
            });

            break;
        }
    }

    return sortFilterData(Object.values(result));
};

/**
 * If the data has a predefined value to sort on, sort based on that. By default they get sorted alphabetically on code so they wouldn't jump around when their amount changes.
 */
const sortFilterData = (filterData: FilterDataType[]): FilterDataType[] => {
    return filterData.sort((a, b) => {
        if (a.sortable && b.sortable) {
            const sortIndexComparison = a.sortable.localeCompare(b.sortable);
            if (sortIndexComparison !== 0) {
                return sortIndexComparison;
            }
        }
        return a.code.localeCompare(b.code);
    });
};

export const hasActiveFilters = (activeFilters: GradeSelectorFiltersType["filters"] | undefined): boolean => {
    return activeFilters ? Object.values(activeFilters).filter((value) => value !== undefined).length > 0 : false;
};

export const getActiveFilterAmount = (activeFilters: GradeSelectorFiltersType["filters"]): number =>
    activeFilters ? Object.values(activeFilters).filter((value) => value !== undefined).length : 0;

export const hasActiveFilter = (
    filterId: GradeSelectorFilterId,
    activeFilters: GradeSelectorFiltersType["filters"] | undefined,
): boolean => {
    const activeCheckboxFilter = !!(isMultipleChoiceFilter(filterId) && activeFilters?.[filterId]);
    const activeRadioFilter = !!(!isMultipleChoiceFilter(filterId) && activeFilters?.[filterId]);

    return activeCheckboxFilter || activeRadioFilter;
};

export const filterButtonLabelMap: Record<GradeSelectorFilterId, GradeSelectorLabelType> = {
    [GradeSelectorFilterId.Colour]: "gradeSelectorFilterColourButton",
    [GradeSelectorFilterId.Engine]: "gradeSelectorFilterEngineButton",
    [GradeSelectorFilterId.WheelDrive]: "gradeSelectorFilterWheeldriveButton",
    [GradeSelectorFilterId.Transmission]: "gradeSelectorFilterTransmissionButton",
    [GradeSelectorFilterId.Price]: "gradeSelectorFilterPriceButton",
    [GradeSelectorFilterId.Sort]: "gradeSelectorFilterSortButton",
    [GradeSelectorFilterId.PopularFeatures]: "gradeSelectorFilterPopularFeaturesButton",
};

export const getResultButtonLabel = (isDisabled: boolean, filterResultAmount: number): GradeSelectorLabelType => {
    if (isDisabled || filterResultAmount === 0) {
        return "gradeSelectorFilterSeeResultsDisabled";
    } else if (filterResultAmount === 1) {
        return "gradeSelectorFilterSeeResult";
    } else {
        return "gradeSelectorFilterSeeResults";
    }
};

/**
 * Helper function for applyFilters which will filter out incompatible engines for a gradeBodyType.
 */
const applyEngineFilters = (grade: GradeBodyTypeType, engines: GradeBodyTypeEngineType[]): GradeBodyTypeType | null => {
    return engines.length > 0
        ? {
              ...grade,
              engines,
          }
        : null;
};

/**
 * Helper function for applyFilters which will filter out incompatible engines and transmissions based on
 * a given array of carId's for a gradeBodyType. This is used for filtering by lcvFilterCars.
 */
export const applyLcvFiltersToGrade = (grade: GradeBodyTypeType, carIds: string[]): GradeBodyTypeType | null => {
    const engines = grade.engines.reduce((filteredEngines: GradeBodyTypeEngineType[], engine) => {
        const filteredTransmissions = engine.transmissions.reduce(
            (fTransmissions: GradeBodyTypeEngineTransmissionType[], transmission) => {
                const filteredWheeldrives = transmission.wheeldrives.filter((wheeldrive) =>
                    carIds.includes(
                        (wheeldrive.car as ApiGradeBodyEngineTransmissionWheeldriveCarType).CarID ||
                            (wheeldrive.car as SBRockarCarType).vehicle.base.externalId,
                    ),
                );

                if (filteredWheeldrives.length > 0) {
                    fTransmissions.push({ ...transmission, wheeldrives: filteredWheeldrives });
                }

                return fTransmissions;
            },
            [],
        );

        if (filteredTransmissions.length > 0) {
            filteredEngines.push({ ...engine, transmissions: filteredTransmissions });
        }

        return filteredEngines;
    }, []);

    return applyEngineFilters(grade, engines);
};

export const applyLcvFilters = (gradeBodyTypes: GradeBodyTypeType[], cars: LcvFilterCarType[]): GradeBodyTypeType[] => {
    const carIds: string[] = [];
    const bodyTypeGradeIds: string[] = [];
    cars.forEach(({ id, bodyTypeId, gradeId }) => {
        carIds.push(id);
        bodyTypeGradeIds.push(`${bodyTypeId}-${gradeId}`);
    });
    return gradeBodyTypes
        .filter((gradeBody) => bodyTypeGradeIds.includes(`${gradeBody.bodyType.id}-${gradeBody.grade.id}`))
        .map((gradeBody) => applyLcvFiltersToGrade(gradeBody, carIds))
        .filter((item): item is GradeBodyTypeType => item !== null);
};

/**
 * Applies the active filters to a list of gradeBodyTypes and returns it.
 */
export const applyFilters = (
    activeFilters: GradeSelectorFiltersType["filters"],
    gradeBodyTypes: GradeBodyTypeType[],
): GradeBodyTypeType[] => {
    const filters: ((grade: GradeBodyTypeType) => GradeBodyTypeType | null)[] = [];

    if (activeFilters.engine) {
        const ecoTag: EcoTagType | undefined = Object.values(EcoTagType).find(
            (value) => value === activeFilters.engine,
        );

        if (ecoTag) {
            filters.push((grade: GradeBodyTypeType): GradeBodyTypeType | null => {
                const engines = grade.engines.filter((engine) => {
                    return (getEcoTagType(engine) ?? "standard").includes(ecoTag);
                });

                return applyEngineFilters(grade, engines);
            });
        }
    }

    if (activeFilters.wheelDrive) {
        filters.push((grade: GradeBodyTypeType): GradeBodyTypeType | null => {
            const engines = grade.engines.filter((engine) => {
                return engine.transmissions.some(({ wheeldrives }) =>
                    wheeldrives.some(({ code }) => code === activeFilters.wheelDrive),
                );
            });

            return applyEngineFilters(grade, engines);
        });
    }

    if (activeFilters.transmission) {
        filters.push((grade: GradeBodyTypeType): GradeBodyTypeType | null => {
            // Map over the engines and filter out the transmissions that don't match the active transmission.
            const engines = grade.engines
                .map((engine) => ({
                    ...engine,
                    transmissions: engine.transmissions.filter(({ code }) => code === activeFilters.transmission),
                }))
                // Filter out the engines that don't have any transmissions left.
                .filter((engine) => engine.transmissions.length > 0);

            return applyEngineFilters(grade, engines);
        });
    }

    if (activeFilters.colour?.length) {
        filters.push((grade: GradeBodyTypeType): GradeBodyTypeType | null => {
            const engines = grade.engines.filter((engine) => {
                return engine.transmissions.some(({ wheeldrives }) =>
                    wheeldrives.some(({ colors }) => colors.some(({ id }) => activeFilters.colour?.includes(id))),
                );
            });

            return applyEngineFilters(grade, engines);
        });
    }

    if (activeFilters.price) {
        filters.push((grade: GradeBodyTypeType): GradeBodyTypeType | null => {
            const engines = grade.engines.filter((engine) => {
                return engine.transmissions.some(({ wheeldrives }) =>
                    wheeldrives.some(({ price }) => {
                        // should be >= lowest price && <= highest price
                        const min = activeFilters.price?.minPrice;
                        const max = activeFilters.price?.maxPrice;
                        if (!price.listWithDiscount || !min || !max) return false;
                        return price.listWithDiscount >= min && price.listWithDiscount <= max;
                    }),
                );
            });
            return applyEngineFilters(grade, engines);
        });
    }

    if (activeFilters.popularFeatures?.length) {
        filters.push((grade: GradeBodyTypeType): GradeBodyTypeType | null => {
            // Check if the grade has any of the selected popular features
            const hasSelectedFeature = grade.equipment.some(
                (feature) => feature.isKeyFeature && activeFilters.popularFeatures?.includes(feature.id.toString()),
            );

            return applyEngineFilters(grade, hasSelectedFeature ? grade.engines : []);
        });
    }

    // Apply all filters first
    const filteredGradeBodyTypes = filters.reduce((arr, filter) => {
        return arr.map(filter).filter((item): item is GradeBodyTypeType => item !== null);
    }, gradeBodyTypes);

    // Then apply sorting if needed
    if (activeFilters.sort) {
        return sortGradeBodyTypes(filteredGradeBodyTypes, activeFilters.sort);
    }

    return filteredGradeBodyTypes;
};

const sortGradeBodyTypes = (gradeBodyTypes: GradeBodyTypeType[], sortCode: string): GradeBodyTypeType[] => {
    return [...gradeBodyTypes].sort((a, b) => {
        switch (sortCode) {
            case SortingType.CashPriceAsc:
                return compareByPrice(a, b, "asc");
            case SortingType.CashPriceDesc:
                return compareByPrice(a, b, "desc");
            case SortingType.WltpAsc:
                return compareBySpec(a, b, "asc", "co2combinedwltp");
            case SortingType.WltpDesc:
                return compareBySpec(a, b, "desc", "co2combinedwltp");
            case SortingType.FuelEfficiencyAsc:
                return compareBySpec(a, b, "asc", "fuelcombinedwltp");
            case SortingType.FuelEfficiencyDesc:
                return compareBySpec(a, b, "desc", "fuelcombinedwltp");

            default:
                return 0;
        }
    });
};

const compareBySpec = (
    a: GradeBodyTypeType,
    b: GradeBodyTypeType,
    direction: "asc" | "desc",
    specName: string,
): number => {
    const specA = getMinSpec(a, specName);
    const specB = getMinSpec(b, specName);

    if (specA === null) return 1;
    if (specB === null) return -1;

    return direction === "asc" ? specA - specB : specB - specA;
};

const getMinSpec = (gradeBodyType: GradeBodyTypeType, specName: string): number | null => {
    let minWltp: number | null = null;

    gradeBodyType.engines.forEach((engine) => {
        engine.transmissions.forEach((transmission) => {
            transmission.wheeldrives.forEach((wheeldrive) => {
                const wltp = wheeldrive.specs.find((spec) => spec.code === specName);
                if (wheeldrive.specs && wltp?.value) {
                    if (minWltp === null || Number(wltp.value) < minWltp) {
                        minWltp = Number(wltp.value);
                    }
                }
            });
        });
    });

    return minWltp;
};

const compareByPrice = (a: GradeBodyTypeType, b: GradeBodyTypeType, direction: "asc" | "desc"): number => {
    const priceA = getMinPrice(a);
    const priceB = getMinPrice(b);

    if (priceA === null) return 1;
    if (priceB === null) return -1;

    return direction === "asc" ? priceA - priceB : priceB - priceA;
};

const getMinPrice = (gradeBodyType: GradeBodyTypeType): number | null => {
    let minPrice: number | null = null;

    gradeBodyType.engines.forEach((engine) => {
        engine.transmissions.forEach((transmission) => {
            transmission.wheeldrives.forEach((wheeldrive) => {
                if (
                    wheeldrive.price.listWithDiscount &&
                    (minPrice === null || wheeldrive.price.listWithDiscount < minPrice)
                ) {
                    minPrice = wheeldrive.price.listWithDiscount;
                }
            });
        });
    });

    return minPrice;
};

export type FilterDataToRenderType = {
    filterId: GradeSelectorFilterId;
    key: GradeSelectorLabelType;
    data: FilterDataType[];
};

/**
 * Prepares the data set in order to render all the available filters all at once in the mobile filter modal.
 * forceFilterId can be provided to only check a certain filter instead of all filters.
 */
export const prepareCombinedFilterData = (
    filteredGradeBodyTypes: GradeBodyTypeType[],
    allGradeBodyTypes: GradeBodyTypeType[],
    activeFilters: GradeSelectorFiltersType["filters"],
    commonSettings: CommonSettingsType,
    gradeSelectorSettings: GradeSelectorSettingsType,
    priceConfig: PriceConfigType,
    forcedFilterId?: GradeSelectorFilterId,
): FilterDataToRenderType[] => {
    return (forcedFilterId ? [forcedFilterId] : Object.values(GradeSelectorFilterId))
        .map((filterId) => {
            // We need to have the filterData according to the already filtered list of gradeBodyTypes.
            // But as all filters are shown in this modal, we also need to check the filterData for the complete unfiltered gradeBodyTypes list.
            // These will be displayed with a disabled state.
            let filterData = getFilterData(
                filterId,
                filteredGradeBodyTypes,
                commonSettings,
                gradeSelectorSettings,
                priceConfig,
            );

            if (filterId === GradeSelectorFilterId.Price) {
                const allPriceData = getFilterData(filterId, allGradeBodyTypes, commonSettings, gradeSelectorSettings, priceConfig);
                const priceRangeFromAll = allPriceData.find((item) => item.code === filterId);

                if (priceRangeFromAll) {
                    const baseFilters = { ...activeFilters };
                    delete baseFilters.price;

                    const filteredWithoutPriceFilter = applyFilters(baseFilters, allGradeBodyTypes);

                    const priceDataWithoutPriceFilter = getFilterData(
                        filterId,
                        filteredWithoutPriceFilter,
                        commonSettings,
                        gradeSelectorSettings,
                        priceConfig,
                    );

                    const availableRange = priceDataWithoutPriceFilter.find((item) => item.code === filterId);

                    if (availableRange) {
                        const displayPriceRange = {
                            ...availableRange,
                            activeMinPrice: activeFilters.price?.minPrice,
                            activeMaxPrice: activeFilters.price?.maxPrice,
                        };

                        filterData = filterData.map((item) => (item.code === filterId ? displayPriceRange : item));
                    }
                }
            }

            if (isMultipleChoiceFilter(filterId)) {
                const baseFilters = { ...activeFilters };
                delete baseFilters[filterId];

                filterData = filterData.map((data) => {
                    const singleOptionFilter = {
                        ...baseFilters,
                        [filterId]: [data.code],
                    };

                    const filteredResults = applyFilters(singleOptionFilter, allGradeBodyTypes);
                    const amount = filteredResults.length;

                    return {
                        ...data,
                        uniqueGradeBodyTypeIds: Array(amount)
                            .fill("")
                            .map((_, index) => `temp-${index}`),
                    };
                });
            }

            const allFilterData = getFilterData(
                filterId,
                allGradeBodyTypes,
                commonSettings,
                gradeSelectorSettings,
                priceConfig,
            )
                .filter(({ code }) => !filterData.find((dat) => dat.code === code))
                .map((data) => {
                    // Note that the filters need to be applied in order to have the result amount, even before a filter is actually clicked on.
                    let amount: number;

                    if (isMultipleChoiceFilter(filterId)) {
                        const baseFilters = { ...activeFilters };
                        delete baseFilters[filterId];

                        const singleOptionFilter = {
                            ...baseFilters,
                            [filterId]: [data.code],
                        };

                        amount = applyFilters(singleOptionFilter, allGradeBodyTypes).length;
                    } else {
                        // For single choice filters, show amount when this filter is added to existing filters
                        amount = applyFilters({ ...activeFilters, [filterId]: data.code }, allGradeBodyTypes).length;
                    }

                    return { ...data, uniqueGradeBodyTypeIds: data.uniqueGradeBodyTypeIds.slice(0, amount) };
                });

            // As mentioned above, active filter data is now concatenated with the remaining filter data as they all need to be shown in this modal.
            // We have to redo the sorting as this is combined filter data where the sorting is no longer valid.
            const data = sortFilterData(filterData.concat(allFilterData));

            const buttonLabel = filterButtonLabelMap[filterId];
            return { key: buttonLabel, data, filterId };
        })
        .filter((filter) => {
            // Don't show filter buttons if they only contain 1 value.
            // Price = excptions: returns a range as 1 value
            // But always show active filters.
            const activeFilter = hasActiveFilter(filter.filterId, activeFilters);
            return !(filter.data.length < 2 && filter.filterId !== GradeSelectorFilterId.Price && !activeFilter);
        });
};

export const isResultButtonDisabled = (
    filterId: GradeSelectorFilterId,
    selectedFilters: string[],
    activeFilters: GradeSelectorFiltersType["filters"] | undefined,
): boolean => {
    const isInvalidRadioFilter =
        selectedFilters.length === 0 || activeFilters?.[filterId]?.toString() === selectedFilters[0];
    const noColourSelected =
        filterId === GradeSelectorFilterId.Colour &&
        !activeFilters?.colour?.length &&
        (!selectedFilters.length || activeFilters?.colour?.toString() === selectedFilters.toString());

    const noPopularFeaturesSelected =
        filterId === GradeSelectorFilterId.PopularFeatures &&
        !activeFilters?.popularFeatures?.length &&
        (!selectedFilters.length || activeFilters?.popularFeatures?.toString() === selectedFilters.toString());

    return (!isMultipleChoiceFilter(filterId) && isInvalidRadioFilter) || noColourSelected || noPopularFeaturesSelected;
};

export const getFilterResultAmount = (
    filterId: GradeSelectorFilterId,
    filterData: FilterDataType[],
    selectedFilters: string[],
    allGradeBodyTypes: GradeBodyTypeType[],
    selectedPriceRange: PriceRangeType | undefined,
    activeFilters: GradeSelectorFiltersType["filters"] | undefined,
): number => {
    if (isMultipleChoiceFilter(filterId)) {
        if (selectedFilters.length === 0) return 0;

        const baseFilters = { ...activeFilters };
        delete baseFilters[filterId];

        const combinedFilter = {
            ...baseFilters,
            [filterId]: selectedFilters,
        };

        const filteredResults = applyFilters(combinedFilter, allGradeBodyTypes);
        return filteredResults.length;
    } else if (filterId === GradeSelectorFilterId.Price) {
        const baseFilters = { ...activeFilters };
        delete baseFilters[filterId];

        // First apply all other active filters
        const preFilteredGradeBodyTypes = applyFilters(baseFilters, allGradeBodyTypes);

        // Then apply the price filter to the already filtered results
        const filteredBodyTypes = preFilteredGradeBodyTypes.filter((gradeBodyType) => {
            return gradeBodyType.engines.some((engine) =>
                engine.transmissions.some((transmission) =>
                    transmission.wheeldrives.some((wheeldrive) => {
                        const price = wheeldrive.price?.listWithDiscount;
                        if (price === undefined || !selectedPriceRange) return false;
                        return price >= selectedPriceRange.minPrice && price <= selectedPriceRange.maxPrice;
                    }),
                ),
            );
        });

        return filteredBodyTypes.length;
    } else {
        return filterData[Number(selectedFilters)]?.uniqueGradeBodyTypeIds?.length ?? 0;
    }
};

export const getCategoryFilterSubCategory = (
    root: string,
    state: GradeSelectorFiltersType,
    category: string | undefined,
    allowClose: boolean,
): string | undefined => {
    const subCategory = category?.split("/")[1];
    const stateContainsSubCategory = !!state.category[root]?.subCategory;

    const closeSubcategory = allowClose && state.category[root]?.subCategory === subCategory;
    const keepSubCategory =
        stateContainsSubCategory && (state.category[root]?.subCategory === category || !subCategory);

    if (closeSubcategory) return undefined;
    if (keepSubCategory) return state.category[root].subCategory;
    return subCategory;
};

export const mergeBodyTypes = (bodyTypes: GradeBodyTypeType[]): GradeBodyTypeType[] =>
    bodyTypes.reduce((acc: GradeBodyTypeType[], current: GradeBodyTypeType) => {
        const existing = acc.find((item) => item.grade.id === current.grade.id);
        if (existing) {
            existing.engines = [...existing.engines, ...current.engines];
        } else {
            acc.push({ ...current });
        }
        return acc;
    }, []);

export const filterLcvFilterCars = (
    cars: LcvFilterCarType[],
    filterValues: Record<string, string>,
): LcvFilterCarType[] => {
    // Use Map to prevent duplicate id's
    return Array.from(
        cars.reduce((carMap, car) => {
            // When a currently selected filterValue isn't present in the filters of the car, the car will be removed.
            // Check if the filterValue code exists inside the car filterValues and if it does see that the value is right
            const carMatchesFilter = Object.keys(filterValues).every((filterValueKey) =>
                car.filterValues.some(
                    (carFilterValue) =>
                        filterValueKey === carFilterValue.code && filterValues[filterValueKey] === carFilterValue.id,
                ),
            );

            if (carMatchesFilter) {
                carMap.set(car.id, car);
            }

            return carMap;
        }, new Map<string, LcvFilterCarType>()),
    ).map(([_, car]) => car);
};

export const setEnabledFilterValues = (
    filteredCars: LcvFilterCarType[],
    currentEnabledFilterValues: string[],
    filterCode: string,
): Record<string, string[]> => {
    return filteredCars.reduce(
        (acc: Record<string, string[]>, car) => {
            car.filterValues.forEach((filterValue) => {
                if (!acc[filterValue.code] || !acc[filterValue.code].includes(filterValue.id)) {
                    acc[filterValue.code] = (acc[filterValue.code] || []).concat(filterValue.id);
                }
            });
            return acc;
        },
        // This makes it so your currently edited filter remains enabled until they filter further
        { [filterCode]: currentEnabledFilterValues },
    );
};

/**
 * Creates a new filterValues object by merging the old filterValues with the new filter action.
 * @param action - The filter action to be added to the filterValues.
 * @param filterValues - The current filterValues.
 * @returns A new filterValues object with the action added to it.
 */
export const getFilteredFilterValues = (
    action: { value: string; filterCode: string },
    filterValues: Record<string, string>,
): Record<string, string> => {
    return {
        ...filterValues,
        [action.filterCode]: action.value,
    };
};
